import { useState, useEffect } from 'react';
import { GraphQLClient } from 'graphql-request';

import { browserLogger } from '@widget/utils/datadog';

const graphQLClient = new GraphQLClient('https://gapi.storyblok.com/v1/api');

export type QueryResult<Data> = {
  data: Data | null;
  loading: boolean;
  error: Error | null;
};

export function useQuery<Data extends Record<string, unknown>>(
  query: string,
  variables?: Record<string, unknown>,
  requestHeaders?: Record<string, string>,
  skipQuery?: boolean,
): QueryResult<Data> {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setLoading(true);

      try {
        const result = await graphQLClient.request<Data>(query, variables, requestHeaders);
        setData(result);
        setError(null);
      } catch (err) {
        setError(err as Error);
        browserLogger.error(err, 'storyblok client query');
      }

      setLoading(false);
    };

    if (!skipQuery) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, JSON.stringify(requestHeaders), JSON.stringify(variables)]);

  return {
    data,
    loading,
    error,
  };
}
